import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFormOptions } from '@ngx-formly/core';
import {
  cloneDeep,
  find,
  get,
  intersection,
  isArray,
  isBoolean,
  isEmpty,
  isNumber,
  parseInt,
  set,
} from 'lodash';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { IField } from '../../../form/model/field';
import { Position } from '../../../form/model/form.model';
import { defaultLang } from '../../../shared/constants';
import { FormService, ISubmitResponse } from '../../form.service';
import { IExtendedFormlyFieldConfig } from '../../model/form.types';
import { v4 as uuid } from 'uuid';
import {
  ContactSelfServiceFormsService,
  ISharedSettings,
} from '../contact-self-service-forms.service';
import {
  IFormTabRendering,
  prepareFormForRendering,
} from '../../utils/prepare-for-rendering';
import { createFormGroupConfigFromRenderingTabs } from '../../utils/create-form-group-config-from-rendering-tabs';
import { ReCaptchaV3Service } from 'ng-recaptcha';

@Component({
  selector: 'app-reg-update-screen',
  templateUrl: './reg-update-screen.component.html',
  styleUrls: ['./reg-update-screen.component.scss'],
})
export class RegUpdateScreenComponent implements OnInit, OnDestroy {
  model = {};
  generatedData = {};
  fields: IExtendedFormlyFieldConfig[];
  form: any;
  tabs: IFormTabRendering[];
  options: FormlyFormOptions[][];
  subscription = new Subscription();
  submitInProgress = false;
  formGroup = new UntypedFormGroup({});
  typeFields: IField[];
  logoBase64: SafeUrl;
  logoPosition = Position;
  selectedIndex = 0;
  token: string;
  loaded: Boolean = true;
  parsedTokenVal: {
    namespace?: string;
    email?: string;
    contactType?: string;
  };
  settings: ISharedSettings;
  contactTip: string = null;
  contactType: string = '';
  formName: string = '';
  objectChooserField = [];
  destroy$ = new Subject();
  conditionalFieldTips = [];
  prevformControlArr = [];
  checkForUpdateLoad: boolean = false;
  browserLang: string;

  constructor(
    protected route: ActivatedRoute,
    protected formService: FormService,
    protected contactSelfService: ContactSelfServiceFormsService,
    protected snackBar: MatSnackBar,
    protected cdr: ChangeDetectorRef,
    protected fb: UntypedFormBuilder,
    protected sanitizer: DomSanitizer,
    protected router: Router,
    protected titleService: Title,
    @Inject('TestRecaptchaService')
    private recaptchaV3Service: ReCaptchaV3Service
  ) {}

  ngOnInit(): void {
    this.browserLang = this.getBrowserLangTranslationString();
    this.titleService.setTitle('Contact self-service');
    this.subscription.add(
      this.route.queryParams.subscribe((params) => {
        this.token = params['token'];
        this.contactType = params['contactType'];
      })
    );
    this.subscription.add(this.fetchDetails());
  }

  fetchDetails(): void {
    this.contactSelfService
      .tokenCheck({ token: this.token })
      .pipe(take(1))
      .subscribe(
        (tokenParsed) => {
          this.parsedTokenVal = tokenParsed ? { ...tokenParsed } : null;
          /**
           * For non-aupr regions, call initForm only if we have fetched site key successfully.
           * If not show error toast message. This message is show only if the domain is not aupr.
           *
           * For aupr regions, checkRecaptchaKeysStatus will always be false.
           */
          if (!this.checkRecaptchaKeysStatus()) {
            this.initForm();
          } else {
            // show the message only if domain is not aupr
            if (this.checkDomainIsNotAUPR()) {
              this.snackBar.open(
                'Unable to connect to reCAPTCHA. Please try again.'
              );
            }
          }
        },
        (err) => {
          this.navigateToExpiryPage();
        }
      );
  }

  getBrowserLangTranslationString() {
    return this.contactSelfService.getBrowserLang();
  }

  checkRecaptchaKeysStatus(): boolean {
    return this.contactSelfService.getRecaptchaKeysStatus;
  }

  checkDomainIsNotAUPR(): boolean {
    return this.contactSelfService.getIsDomainNotAupr;
  }

  initForm(): void {
    this.subscription.add(
      this.route.url
        .pipe(
          switchMap((url: any) => {
            if (url) {
              return this.contactSelfService.fetchSchema(
                url[0]?.path,
                this.parsedTokenVal?.contactType,
                this.browserLang,
                this.parsedTokenVal?.email,
                this.token
              );
            }
          })
        )
        .subscribe(
          (form: any) => {
            if (form) {
              this.loadForm(form);
            } else {
              this.navigateToExpiryPage();
            }
          },
          () => {
            this.navigateToExpiryPage();
          }
        )
    );
  }

  loadForm(form): void {
    this.form = form;
    if (this.form.logo) {
      this.logoBase64 = 'data:image/svg+xml;base64,' + this.form.logo;
    }
    this.settings = {
      contactType: this.parsedTokenVal?.contactType,
      logoPosition: form?.logoPositioning,
      background: form?.background,
      logoBase64: this.logoBase64,
      defaultContent: form?.defaultContent,
      footerContent: form?.footerContent,
      headerContent: form?.registrationScreenContent
        ? form?.registrationScreenContent
        : form?.updateScreenContent,
      successContent: form?.registrationConfirmationScreenContent
        ? form?.registrationConfirmationScreenContent
        : form?.updateConfirmationScreenContent,
    };
    this.buildFormlyOptions();
    this.createTypeFields(form);
    this.render();
    this.sortFieldsWithType();
    this.cdr.detectChanges();
    // Once the form is rendered populate fields with values
    this.model = { ...this.model, ...this.generatedData };
    this.cdr.detectChanges(); // had to manually call detect changes, so that the model and form are initialized before below method is called
    this.clearFieldsBasedOnVisibilityChanges();
  }

  /**
   * This method mainly works on the logic if conditional field is present on the form or not.
   * To check if field is present on the form or not, scan the formGroup object.
   * The method performs below functionalities -
   * 1. checks if there are any conditional fields which are dependent on parent fields.
   * 2. If yes, apply valueChanges on fieldTip(parent field) on which the conditional field is dependent on.
   * 3. If parent's value changes and due to which conditional field is show or hidden on the form, then conditional fields value is cleared.
   * 4. For the first load of the update form, no value is cleared from the conditional fields
   */
  clearFieldsBasedOnVisibilityChanges(): void {
    // store tips of the form when form is loaded
    this.prevformControlArr = Object.keys(this.formGroup.value);
    this.tabs?.forEach((tab) => {
      tab?.fields?.forEach((field) => {
        field?.fieldGroup?.forEach((fieldGroup) => {
          if (isEmpty(fieldGroup?.fieldGroup)) {
            this.setWatchOnConditionalFormControl(fieldGroup);
          } else {
            fieldGroup?.fieldGroup?.forEach((fieldGroup) => {
              this.setWatchOnConditionalFormControl(fieldGroup);
              this.setWatchOnConditionalFormControl(fieldGroup);
            });
          }
        });
      });
    });
  }

  setWatchOnConditionalFormControl(
    fieldGroup: IExtendedFormlyFieldConfig
  ): void {
    if (get(fieldGroup, 'isConditional', false)) {
      if (
        get(fieldGroup, 'conditionalFields.visibility.value', '') ===
        'conditional'
      ) {
        this.conditionalFieldTips.push(fieldGroup?.key);
        const includes = get(
          fieldGroup,
          'conditionalFields.visibility.conditions.includes',
          []
        );
        if (includes.length > 0) {
          includes.forEach((obj) => {
            const fieldTip = get(obj, 'fieldTip', null);
            const values = get(obj, 'values', []);
            if (fieldTip && values.length > 0) {
              fieldGroup?.form
                ?.get(fieldTip)
                ?.valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe((_) => {
                  this.clearFields();
                });
            }
          });
        }
      }
    }
  }

  //Below lines of code are used to clear out conditional field value when this field is shown or hidden
  clearFields(): void {
    const curformControlArr = Object.keys(this.formGroup.value);
    const intersectedValues = intersection(
      this.prevformControlArr,
      curformControlArr
    );
    // if any tip of conditionalFieldTips is not present in intersectedValues, then that field was hidden or shown on the form
    this.conditionalFieldTips.forEach((key) => {
      if (intersectedValues.indexOf(key) < 0) {
        const obj = find(this.typeFields, { $tip: key });
        // below lines clear the field and set it to there default values
        if (obj) {
          if (obj?.type === 'boolean') {
            this.formGroup.get(key)?.patchValue(false);
            set(this.model, key, false);
          } else if (obj?.type === 'number') {
            this.formGroup.get(key)?.patchValue(0);
            set(this.model, key, 0);
          } else if (
            obj?.format === 'list-items-select' ||
            obj?.format === 'object-chooser' ||
            obj?.format === 'file-upload' ||
            obj?.format === 'date-time'
          ) {
            this.formGroup.get(key)?.patchValue(null);
            set(this.model, key, null);
          } else {
            this.formGroup.get(key)?.patchValue('');
            set(this.model, key, '');
          }
        }
      }
    });
    // update prevformControlArr with current form field tips
    this.prevformControlArr = Object.keys(this.formGroup.value);
  }

  getPathName(): any {
    return get(window, 'location.pathname', null);
  }

  submitForm(): void {
    if (this.formGroup.valid) {
      let submitModel = cloneDeep(this.model);
      let submitter$: Observable<ISubmitResponse>;
      submitter$ = this.contactSelfService
        .getReCaptchaV3Token$(this.recaptchaV3Service)
        .pipe(
          switchMap((recaptChaV3Token) => {
            return this.contactSelfService.submitForm$(
              this.form.screenFormData.tip,
              submitModel,
              this.typeFields,
              recaptChaV3Token,
              this.token
            );
          })
        );
      this.snackBar.open('Submitting form');
      this.submitInProgress = true;
      this.cdr.detectChanges();

      this.subscription.add(
        submitter$
          .pipe(
            catchError((error) => {
              this.submitInProgress = false;
              this.cdr.detectChanges();
              this.handleSubmitError(null, error);
              return of(null);
            }),
            map((response: any) => {
              this.submitInProgress = false;
              this.cdr.detectChanges();
              if (response) {
                if (response.success) {
                  this.snackBar.dismiss();
                  this.contactSelfService.updateCurrentsharedFormSettings(
                    this.settings
                  );
                  this.router.navigate([
                    './contact-form',
                    'self-service-acknowledgment-screen',
                  ]);
                } else {
                  this.handleSubmitError(response);
                }
              }
            })
          )
          .subscribe(() => {})
      );
    } else {
      console.log('the form has invalid fields');
    }
  }

  createTypeFields(form: any): void {
    const fields: IField[] = [];
    form.screenFormData.tabs.forEach((tab) => {
      const userLang = this.browserLang;
      tab.tabName = get(
        tab,
        `i18n_tabName.${userLang}`,
        get(tab, `i18n_tabName.${defaultLang}`, get(tab, 'tabName'))
      );
      tab.rows.forEach((row) => {
        row.fields.forEach((field) => {
          const fieldTip = get(field, 'templateOptions.contextTypeTip');
          if (isEmpty(fieldTip)) {
            field.templateOptions.contextTypeTip = uuid();
          }
          if (
            isEmpty(get(field, 'templateOptions.appearance')) &&
            get(field, 'type') !== 'text-content' &&
            get(field, 'type') !== 'section-header' &&
            get(field, 'type') !== 'checkbox'
          ) {
            field.templateOptions.appearance = 'outline';
          }
          const fieldMap: IField = {
            label: get(
              field,
              `templateOptions.i18n_label.${userLang}`,
              get(field, `templateOptions.i18n_label.${defaultLang}`, '')
            ),
            $tip: get(field, 'templateOptions.contextTypeTip'),
            isDateOnly: get(field, 'templateOptions.type', null) === 'date', // we use this later on... before submitting the data
            format: get(field, 'type'),
          };
          fields.push(fieldMap);
        });
      });
    });
    this.typeFields = fields;
  }

  handleSubmitError(response?: ISubmitResponse, error?: any): void {
    let snackBarRef;
    if (response) {
      console.log('[FormComponent/handleSubmitError] Response', { response });
    }
    if (error) {
      console.log('[FormComponent/handleSubmitError] Thrown error', { error });
    }
    if (
      error?.error?.message === 'Failed to validate captcha' ||
      error?.error?.message === 'Captcha received is invalid' ||
      error?.message?.indexOf('Invalid reCAPTCHA') > -1
    ) {
      snackBarRef = this.snackBar.open(
        'Unable to connect to reCAPTCHA.',
        'Please try again'
      );
    } else {
      snackBarRef = this.snackBar.open('Could not submit form', 'Try again');
    }
    this.subscription.add(
      snackBarRef.onAction().subscribe(() => snackBarRef.dismiss())
    );
  }

  render(): void {
    if (this.form) {
      let tabs: IFormTabRendering[];
      this.formName = this.form.screenFormData
        ? this.form.screenFormData?.name ?? ''
        : '';
      const formTabs = cloneDeep(this.form.screenFormData?.tabs);
      const typeFieldsWithObjectSchemaProperties =
        this.formService.mergeObjectSchemaWithFormFields(
          formTabs,
          this.typeFields
        );
      tabs = prepareFormForRendering({
        tabs: formTabs,
        typeFields: typeFieldsWithObjectSchemaProperties,
      });

      const config = createFormGroupConfigFromRenderingTabs(tabs);
      this.formGroup = this.fb.group(config);
      if (!isEmpty(this.form?.contactData)) {
        // create a data to patch the value to formGroup
        this.generatedData = this.form.contactData.fields.reduce(
          (acc, field) => {
            if (field?.tip) {
              const obj = find(this.typeFields, { $tip: field?.tip });
              if (obj) {
                if (obj?.type === 'boolean') {
                  if (isBoolean(field.value[0])) {
                    acc[field.tip] = field.value[0];
                  } else {
                    acc[field.tip] = field.value[0] === 'true' ? true : false;
                  }
                } else if (obj?.type === 'number') {
                  acc[field.tip] = isNumber(field.value[0])
                    ? field.value[0]
                    : parseInt(field.value[0]);
                } else if (
                  obj?.type === 'array' ||
                  obj?.format === 'file-upload'
                ) {
                  if (obj?.type === 'string') {
                    // Single File upload field
                    acc[field.tip] = field.value[0];
                  } else {
                    acc[field.tip] = field.value
                      ? isArray(field.value)
                        ? field.value
                        : [field.value]
                      : [];
                  }
                } else {
                  acc[field.tip] = field.value[0];
                }
              }
            }
            return acc;
          },
          {}
        );
        this.contactTip = this.form?.contactData?.tip;
      } else {
        // if it has an email field, populate it with email value fetched from JWT token
        formTabs?.forEach((tab) => {
          tab?.rows?.forEach((row) => {
            row?.fields?.forEach((field) => {
              if (
                get(field, 'type', '') === 'email-address' &&
                get(field, 'templateOptions.implements', []).indexOf(
                  'app/super-type/contact:email'
                ) > -1
              ) {
                const key = get(field, 'templateOptions.contextTypeTip');
                this.model = {
                  [key]: this.parsedTokenVal?.email,
                };
                return; // exit the loop
              }
            });
          });
        });
      }
      this.tabs = tabs;
    }
  }

  buildFormlyOptions(): void {
    this.options = this.form.screenFormData.tabs.map((tab) =>
      tab.rows.map(() => ({} as FormlyFormOptions))
    );
  }

  moveToNextTab(): void {
    this.selectedIndex = this.selectedIndex + 1;
  }

  navigateToExpiryPage(): void {
    if (this.contactType) {
      this.contactSelfService.updateCurrentContactTypeData(this.contactType);
    }
    this.router.navigate(['./contact-form', 'self-service-expired']);
  }

  /*
   * sortFieldsWithType() is to find only object chooser field and push it to an array.
   */
  sortFieldsWithType(): void {
    this.form?.screenFormData?.tabs?.map((tab) => {
      tab?.rows?.map((row) => {
        row.fields.filter((field) => {
          if (field.type === 'object-chooser') {
            this.objectChooserField.push(field.templateOptions.contextTypeTip);
          }
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
